import React from 'react';
import { InputNumber } from 'antd';
import type { FJ750 } from '../../../services/pwj220Api';
import LookupField from '../../../components/LookupField';

interface Props {
  formData: Partial<FJ750>;
  isEditing: boolean;
  onChange: (field: keyof FJ750, value: any) => void;
}

const BONDING_TYPES: Record<number, string> = {
  1: '針織貼針織',
  2: '針織貼平織',
  3: '平織貼平織',
  4: '平織貼針織',
  5: '其他',
};

const Tab1BasicInfo: React.FC<Props> = React.memo(({ formData, isEditing, onChange }) => {
  const val = (f: keyof FJ750) => (formData[f] ?? '') as string;
  const numVal = (f: keyof FJ750) => (formData[f] ?? 0) as number;

  return (
    <div className="form-grid">
      {/* 分析單號 */}
      <label>分析單號</label>
      <input data-contract-field="fj750_01" value={val('fj750_01')} readOnly className="form-input readonly" />

      {/* 入單日期 (readOnly per FoxPro contract) */}
      <label>入單日期</label>
      <input
        value={val('fj750_12') ? val('fj750_12').substring(0, 10) : ''}
        readOnly
        className="form-input readonly"
      />

      {/* 開發單號 (hasDblClick → LookupField) */}
      <label>開發單號</label>
      <LookupField
        lookupType="salesDevelopment"
        value={val('fj750_11')}
        onChange={(code) => onChange('fj750_11', code)}
        disabled={!isEditing}
        title="選擇開發單號"
      />

      {/* 產品用途 (fj750_13, Page1 - hasDblClick → LookupField) */}
      <label>產品用途</label>
      <LookupField
        lookupType="productUsage"
        value={val('fj750_13')}
        onChange={(code) => onChange('fj750_13', code)}
        disabled={!isEditing}
        title="選擇產品用途"
      />

      {/* 業務分析單號 (readOnly per FoxPro contract) */}
      <label>業務分析單號</label>
      <input
        value={val('fj750_76')}
        readOnly
        className="form-input readonly"
      />

      {/* 產品代號 (前6碼 + 後6碼) */}
      <label>產品代號</label>
      <div className="inline-group">
        <input
          data-contract-field="fj750_161"
          value={val('fj750_161')}
          maxLength={6}
          onChange={e => onChange('fj750_161', e.target.value)}
          disabled={!isEditing}
          className="form-input code6"
          placeholder="前6碼"
        />
        <input
          value={val('fj750_162')}
          maxLength={6}
          onChange={e => onChange('fj750_162', e.target.value)}
          disabled={!isEditing}
          className="form-input code6"
          placeholder="後6碼"
        />
      </div>

      {/* 產品名稱 */}
      <label>產品名稱</label>
      <textarea
        value={val('fj750_17')}
        onChange={e => onChange('fj750_17', e.target.value)}
        disabled={!isEditing}
        className="form-textarea"
        rows={2}
      />

      {/* 廠商收件單號 (hasDblClick → LookupField) */}
      <label>廠商收件單號</label>
      <div className="inline-group">
        <LookupField
          lookupType="sampleReceipt"
          value={val('fj750_14')}
          onChange={(code) => onChange('fj750_14', code)}
          disabled={!isEditing}
          title="選擇廠商收件單號"
        />
        <LookupField
          lookupType="sampleReceiptItem"
          value={String(numVal('fj750_15'))}
          onChange={(code) => onChange('fj750_15', parseInt(code) || 0)}
          disabled={!isEditing}
          title="選擇項次"
        />
      </div>

      {/* 貼合類別 */}
      <label>貼合類別</label>
      <div className="inline-group">
        <select
          value={numVal('fj750_20')}
          onChange={e => onChange('fj750_20', parseInt(e.target.value))}
          disabled={!isEditing}
          className="form-select"
        >
          {Object.entries(BONDING_TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {numVal('fj750_20') === 5 && (
          <input
            value={val('fj750_201')}
            onChange={e => onChange('fj750_201', e.target.value)}
            disabled={!isEditing}
            className="form-input"
            placeholder="說明"
          />
        )}
      </div>

      {/* 產品開發數量/單位 (fj750_18 + fj750_19, Page1) */}
      <label>產品開發數量/單位</label>
      <div className="inline-group">
        <InputNumber
          value={numVal('fj750_18')}
          onChange={v => onChange('fj750_18', v || 0)}
          disabled={!isEditing}
          className="form-input"
        />
        <input
          value={val('fj750_19')}
          maxLength={3}
          onChange={e => onChange('fj750_19', e.target.value)}
          disabled={!isEditing}
          className="form-input narrow"
          placeholder="單位"
        />
      </div>

      {/* 繳交研發處之布樣數量/單位 (fj750_53 + fj750_54, Page1) */}
      <label>繳交研發處之布樣數量/單位</label>
      <div className="inline-group">
        <InputNumber
          value={numVal('fj750_53')}
          onChange={v => onChange('fj750_53', v || 0)}
          disabled={!isEditing}
          className="form-input"
        />
        <input
          value={val('fj750_54')}
          maxLength={3}
          onChange={e => onChange('fj750_54', e.target.value)}
          disabled={!isEditing}
          className="form-input narrow"
          placeholder="單位"
        />
      </div>

      {/* 入單人員 */}
      <label>入單人員</label>
      <input value={val('fj750_62')} readOnly className="form-input readonly" />

      {/* 核准狀態 */}
      <label>核准狀態</label>
      <input
        value={formData.fj750_80 === 'P' ? '已核准' : '未核准'}
        readOnly
        className={`form-input readonly ${formData.fj750_80 === 'P' ? 'status-approved' : ''}`}
      />

      {/* 公告開始日 */}
      <label>公告開始日</label>
      <input
        type="date"
        value={val('fj750_77') ? val('fj750_77').substring(0, 10) : ''}
        onChange={e => onChange('fj750_77', e.target.value ? e.target.value + 'T00:00:00Z' : null)}
        disabled={!isEditing}
        className="form-input"
      />

      {/* 公告終止日 */}
      <label>公告終止日</label>
      <input
        type="date"
        value={val('fj750_78') ? val('fj750_78').substring(0, 10) : ''}
        onChange={e => onChange('fj750_78', e.target.value ? e.target.value + 'T00:00:00Z' : null)}
        disabled={!isEditing}
        className="form-input"
      />

      {/* 針孔規格 */}
      <label>針孔-外 INCH</label>
      <InputNumber
        data-contract-field="fj750_171"
        value={numVal('fj750_171')}
        onChange={v => onChange('fj750_171', v || 0)}
        disabled={!isEditing}
        className="form-input"
      />

      <label>針孔-內 INCH</label>
      <InputNumber
        data-contract-field="fj750_172"
        value={numVal('fj750_172')}
        onChange={v => onChange('fj750_172', v || 0)}
        disabled={!isEditing}
        className="form-input"
      />

      <label>G/Y 碼重</label>
      <InputNumber
        data-contract-field="fj750_173"
        value={numVal('fj750_173')}
        onChange={v => onChange('fj750_173', v || 0)}
        disabled={!isEditing}
        className="form-input"
      />

      <label>GM/SQM 米平方重</label>
      <InputNumber
        data-contract-field="fj750_174"
        value={numVal('fj750_174')}
        onChange={v => onChange('fj750_174', v || 0)}
        disabled={!isEditing}
        className="form-input"
      />
    </div>
  );
});

export default Tab1BasicInfo;
